import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, X, ZoomIn } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "./ui/carousel";
import { useLang } from "../i18n";

const Lightbox = ({ items, index, setIndex, onClose, t }) => {
  const [zoom, setZoom] = useState(false);
  const [origin, setOrigin] = useState("50% 50%");
  const it = items[index];

  const next = useCallback(() => setIndex((i) => (i + 1) % items.length), [items.length, setIndex]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + items.length) % items.length), [items.length, setIndex]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, next, prev]);

  useEffect(() => setZoom(false), [index]);

  const onMove = (e) => {
    if (!zoom) return;
    const r = e.currentTarget.getBoundingClientRect();
    setOrigin(`${(((e.clientX - r.left) / r.width) * 100).toFixed(1)}% ${(((e.clientY - r.top) / r.height) * 100).toFixed(1)}%`);
  };

  const btnCls =
    "flex h-12 w-12 items-center justify-center rounded-full border border-brand-bone/30 text-brand-bone transition-colors duration-300 hover:border-brand-timber hover:text-brand-timber";

  return createPortal(
    <motion.div
      data-testid="lightbox-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="fixed inset-0 z-[100] flex flex-col bg-brand-blue/[0.97] backdrop-blur-sm"
      onClick={onClose}
    >
      <div className="flex items-center justify-between px-6 py-4 md:px-10" onClick={(e) => e.stopPropagation()}>
        <p data-testid="lightbox-counter" className="font-mono text-xs uppercase tracking-[0.2em] text-brand-bone/60">
          {String(index + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
        </p>
        <div className="flex items-center gap-4">
          <span className="hidden items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-brand-bone/50 md:flex">
            <ZoomIn size={14} /> {t("detail.zoomHint")}
          </span>
          <button data-testid="lightbox-close" onClick={onClose} aria-label="Close" className={btnCls}>
            <X size={20} />
          </button>
        </div>
      </div>

      <div className="relative flex min-h-0 flex-1 items-center justify-center px-4 pb-2 md:px-20">
        {items.length > 1 && (
          <button
            data-testid="lightbox-prev"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Previous"
            className={btnCls + " absolute left-4 z-10 md:left-8"}
          >
            <ArrowLeft size={20} />
          </button>
        )}

        <div
          className={`max-h-full overflow-hidden rounded-sm ${zoom ? "cursor-zoom-out" : "cursor-zoom-in"}`}
          onClick={(e) => { e.stopPropagation(); setZoom((z) => !z); }}
          onMouseMove={onMove}
        >
          <img
            data-testid="lightbox-image"
            src={it.src}
            alt={it.caption}
            className="max-h-[72vh] w-auto max-w-full select-none object-contain transition-transform duration-300 ease-out"
            style={{ transform: zoom ? "scale(2.4)" : "scale(1)", transformOrigin: origin }}
            draggable={false}
          />
        </div>

        {items.length > 1 && (
          <button
            data-testid="lightbox-next"
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Next"
            className={btnCls + " absolute right-4 z-10 md:right-8"}
          >
            <ArrowRight size={20} />
          </button>
        )}
      </div>

      <p
        data-testid="lightbox-caption"
        onClick={(e) => e.stopPropagation()}
        className="px-6 pb-8 text-center font-mono text-[11px] uppercase tracking-[0.2em] text-brand-bone/70"
      >
        {it.caption}
      </p>
    </motion.div>,
    document.body
  );
};

export const SpeciesGallery = ({ species }) => {
  const { t } = useLang();
  const [api, setApi] = useState(null);
  const [current, setCurrent] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!api) return;
    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
      setCanPrev(api.canScrollPrev());
      setCanNext(api.canScrollNext());
    };
    onSelect();
    api.on("select", onSelect);
    api.on("reInit", onSelect);
    return () => {
      api.off("select", onSelect);
      api.off("reInit", onSelect);
    };
  }, [api]);

  if (!species.gallery?.length) return null;

  return (
    <section data-testid="species-gallery" className="mx-auto max-w-[1600px] px-6 pb-24 md:px-12">
      <div className="mb-8 flex items-end justify-between">
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-brand-timber">
            {t("detail.gallery")}
          </span>
          <p className="mt-2 font-mono text-xs text-brand-slate" data-testid="gallery-counter">
            {String(current + 1).padStart(2, "0")} / {String(species.gallery.length).padStart(2, "0")}
          </p>
        </div>
        <div className={`flex gap-2 ${!canPrev && !canNext ? "hidden" : ""}`}>
          <button
            data-testid="gallery-prev-btn"
            onClick={() => api?.scrollPrev()}
            disabled={!canPrev}
            aria-label="Previous image"
            className="flex h-12 w-12 items-center justify-center rounded-full border border-brand-blue/30 text-brand-blue transition-colors duration-300 hover:border-brand-timber hover:text-brand-timber disabled:cursor-default disabled:opacity-30 disabled:hover:border-brand-blue/30 disabled:hover:text-brand-blue"
          >
            <ArrowLeft size={18} />
          </button>
          <button
            data-testid="gallery-next-btn"
            onClick={() => api?.scrollNext()}
            disabled={!canNext}
            aria-label="Next image"
            className="flex h-12 w-12 items-center justify-center rounded-full border border-brand-blue/30 text-brand-blue transition-colors duration-300 hover:border-brand-timber hover:text-brand-timber disabled:cursor-default disabled:opacity-30 disabled:hover:border-brand-blue/30 disabled:hover:text-brand-blue"
          >
            <ArrowRight size={18} />
          </button>
        </div>
      </div>

      <Carousel setApi={setApi} opts={{ align: "start", loop: true }} className="w-full">
        <CarouselContent className="-ml-4">
          {species.gallery.map((g, i) => (
            <CarouselItem key={i} className="pl-4 basis-full sm:basis-1/2 lg:basis-1/3" data-testid={`gallery-slide-${i}`}>
              <figure className="group h-full">
                <button
                  data-testid={`gallery-open-${i}`}
                  onClick={() => setLightbox(i)}
                  aria-label={`View ${g.caption}`}
                  className="relative block w-full cursor-zoom-in overflow-hidden rounded-sm border border-border bg-secondary"
                >
                  <img
                    src={g.src}
                    alt={g.caption}
                    loading="lazy"
                    className="h-[420px] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <span className="absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-full bg-brand-blue/70 text-brand-bone opacity-0 backdrop-blur-sm transition-opacity duration-300 group-hover:opacity-100">
                    <ZoomIn size={16} />
                  </span>
                </button>
                <figcaption className="mt-3 font-mono text-[11px] uppercase tracking-[0.15em] text-brand-slate">
                  {g.caption}
                </figcaption>
              </figure>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      <AnimatePresence>
        {lightbox != null && (
          <Lightbox
            items={species.gallery}
            index={lightbox}
            setIndex={setLightbox}
            onClose={() => setLightbox(null)}
            t={t}
          />
        )}
      </AnimatePresence>
    </section>
  );
};
